import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { Box, CircularProgress, Typography, Alert } from "@mui/material";
import { useTranslation } from "react-i18next";
import { SessionSorageInstance } from "../../Services/SessionStorageService";
import PartnerService from "../../Services/http/PartnerService";
import { setUser, setuserRol } from "../../Redux/GeneralSlice";
import { setDefaultAccount } from "../../Redux/AccountSlice";
import { partnerLogin, setPartnerContext } from "../../Redux/PartnerSlice";
import { ensurePartnerDefaultLanguage } from "../../Redux/LanguageSlice";
import { TLoginResponse } from "../../types/Login.type";
import OnboardingLayout from "../Layout/OnboardingLayout";
import PartnerAccountNameForm from "./PartnerAccountNameForm";

/**
 * Represents the business state of the Partner onboarding bridge.
 * This is not a "step wizard" — it is a state machine for the page flow.
 */
type OnboardingState =
  | "validating_session" // validating OTT + starting Partner session
  | "account_name_required" // business rule: Partner must set account_name to continue
  | "finalizing_login" // persisting session + dispatching redux + navigating
;
const PartnerOnboardingBridge = () => {
  const { t } = useTranslation("partnerOnboardingBridge");

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [state, setState] = useState<OnboardingState>("validating_session");
  const [error, setError] = useState<string | null>(null);

  const [sessionData, setSessionData] = useState<TLoginResponse | null>(null);
  const [isSubmittingName, setIsSubmittingName] = useState(false);

  const handleSessionSuccess = useCallback(
    (data: TLoginResponse) => {
      try {
        setState("finalizing_login");

        SessionSorageInstance.setUserDetails(data);

        dispatch(setuserRol(SessionSorageInstance.getUserRole()));
        dispatch(setUser(SessionSorageInstance.getUserDetails()));
        dispatch(setDefaultAccount(data.account));

        if (data.needs_approval) {
          navigate("/appApproval");
        } else {
          navigate("/layout");
        }
      } catch (err) {
        const errorMsg =
          err instanceof Error ? err.message : t("errors.processingSession");
        setError(errorMsg);
      }
    },
    [dispatch, navigate, t]
  );

  const validateAndInitializeSession = useCallback(async (ottToken: string) => {
    try {
      setState("validating_session");
      setError(null);

      const response = await PartnerService.startSession({ ott: ottToken });

      if (!response || !response.token) {
        throw new Error(t("errors.invalidResponse"));
      }

      setSessionData(response);
      dispatch(setPartnerContext(response.partner));

      // Business rule: if account_name is missing, user must provide it before continuing.
      if (response.is_account_name_set) {
        handleSessionSuccess(response);
      } else {
        setState("account_name_required");
        SessionSorageInstance.setUserDetails(response);
      }
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : t("errors.unknown");
      setError(errorMsg);
    }
  },
    [handleSessionSuccess, t]
  );

  useEffect(() => {
    // Partner onboarding flow: ensure the default language is Chinese (if not already set).
    dispatch(ensurePartnerDefaultLanguage());

    // Initialize any Partner-specific redux/session state.
    dispatch(partnerLogin());

    const ottToken = searchParams.get("ott");
    if (!ottToken) {
      setError(t("errors.missingOtt"));
      return;
    }

    validateAndInitializeSession(ottToken);
  }, [dispatch, searchParams, t, validateAndInitializeSession]);

  const handleAccountNameSubmit = useCallback(async (accountName: string) => {
    if (!sessionData) {
      setError(t("errors.sessionMissing"));
      return;
    }

    try {
      setIsSubmittingName(true);
      setError(null);

      await PartnerService.setAccountName({ account_name: accountName });

      // Update the local session state to reflect the newly set account name.
      const updatedSessionData: TLoginResponse = {
        ...sessionData,
        is_account_name_set: true,
      };

      setSessionData(updatedSessionData);
      handleSessionSuccess(updatedSessionData);
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : t("errors.unknown");
      setError(errorMsg);
    // Keep the user on the "account_name_required" business state to retry.
      setState("account_name_required");
    }
    finally {
      setIsSubmittingName(false);
    }
  },
    [handleSessionSuccess, sessionData, t]
  );

  return (
    <OnboardingLayout rightClassName="center-items">
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        width="100%"
        p={3}
      >
        {state === "validating_session" && (
          <Box textAlign="center">
           {!error && (<CircularProgress size={60} sx={{ mb: 3 }} />)}
            <Typography variant="h6" sx={{ mb: 2 }}>
              {t("validating.title")}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {t("validating.subtitle")}
            </Typography>
          </Box>
        )}

        {state === "account_name_required" && sessionData && (
          <PartnerAccountNameForm
            onSubmit={handleAccountNameSubmit}
            isLoading={isSubmittingName}
            error={error}
          />
        )}

        {state === "finalizing_login" && (
          <Box textAlign="center">
            {!error && (<CircularProgress size={60} sx={{ mb: 3 }} />)}
            <Typography variant="h6" sx={{ mb: 2 }}>
              {t("success.title")}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {t("success.subtitle")}
            </Typography>
          </Box>
        )}

        {error && (
          <Box width="100%" maxWidth={400}>
            <Alert severity="error" sx={{ mb: 2 }}>
              <Typography variant="h6" sx={{ mb: 1 }}>
                {t("errorBanner.title")}
              </Typography>
              <Typography variant="body2">{error}</Typography>
            </Alert>
            <Typography variant="body2" color="textSecondary">
              {t("errorBanner.hint")}
            </Typography>
          </Box>
        )}
      </Box>
    </OnboardingLayout>
  );
};

export default PartnerOnboardingBridge;